import { useEffect, useState } from "react";
import { Plus, Settings2 } from "lucide-react";
import {
  displayValue,
  isGlobalProfileCollection,
  type Resource,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { ProfileDatasetEditor } from "./ProfileDatasetEditor";
import { ProfileGrid, type ProfileGridRow } from "./ProfileGrid";
import { Button } from "./components";

/**
 * Every global profile in the workspace as one grid. Profile types are the
 * global profile collections configured on the Global profile data page.
 */
export function GlobalProfileGrid({
  tenantId,
  notify,
  onConfigure,
}: {
  tenantId: string;
  notify(message: string): void;
  onConfigure?(): void;
}) {
  const services = useAuthoringServices();
  const [types, setTypes] = useState<Resource[]>([]),
    [rows, setRows] = useState<ProfileGridRow[]>([]),
    [loading, setLoading] = useState(true),
    [reload, setReload] = useState(0),
    [type, setType] = useState(""),
    [editing, setEditing] = useState<{
      resource: Resource;
      recordId?: string;
      startNew?: boolean;
    }>();
  useEffect(() => {
    let active = true;
    setLoading(true);
    void (async () => {
      const all = ((await services.listResources?.()) || []).filter(
        (row) =>
          row.tenantId === tenantId &&
          !row.archived &&
          isGlobalProfileCollection(row),
      );
      const latest = all.filter(
        (row) =>
          !all.some(
            (other) => other.id === row.id && other.version > row.version,
          ),
      );
      const next: ProfileGridRow[] = [];
      for (const resource of latest) {
        const dataset = await services.profileDataset?.(resource);
        for (const record of dataset?.records || [])
          next.push({
            id: record.id,
            name: record.name,
            typeId: resource.id,
            typeName: resource.name,
            status: record.active ? "Active" : "Inactive",
            searchText: Object.values(record.values)
              .map((value) => displayValue(value))
              .join(" "),
          });
      }
      if (!active) return;
      setTypes(latest);
      setRows(next);
    })()
      .catch((error) => active && notify((error as Error).message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [services.listResources, services.profileDataset, tenantId, reload]);
  const newType = types.find((row) => row.id === type) || types[0];
  return (
    <>
      {loading && <p role="status">Loading global profiles…</p>}
      <ProfileGrid
        label="Global profiles"
        rows={rows}
        types={types.map((row) => ({ id: row.id, name: row.name }))}
        type={type}
        onType={setType}
        empty={
          types.length
            ? "No global profiles yet. Add one to get started."
            : "No profile types yet. Set one up in Global profile data first."
        }
        onOpen={(row) => {
          const resource = types.find((item) => item.id === row.typeId);
          if (resource) setEditing({ resource, recordId: row.id });
        }}
        actions={
          <>
            {onConfigure && (
              <Button onClick={onConfigure}>
                <Settings2 size={13} /> Profile types
              </Button>
            )}
            <Button
              kind="primary"
              disabled={!newType || !services.saveProfileDataset}
              title={
                newType ? `New ${newType.name} profile` : "Add a profile type"
              }
              onClick={() =>
                newType && setEditing({ resource: newType, startNew: true })
              }
            >
              <Plus size={13} /> New profile
            </Button>
          </>
        }
      />
      {editing && (
        <ProfileDatasetEditor
          key={`${editing.resource.id}:${editing.recordId || "new"}`}
          resource={editing.resource}
          initialRecordId={editing.recordId}
          startNew={editing.startNew}
          onClose={() => {
            setEditing(undefined);
            setReload((value) => value + 1);
          }}
        />
      )}
    </>
  );
}
